//@ts-nocheck
/* eslint-disable */
import React, { useEffect, useState } from 'react';
import { useNav } from 'src/onboarding/hooks/nav';

import styles from './style.module.scss';
import { useRemoteConfig } from 'core/hooks/use-remote-config';
import { useUpsaleMethods } from 'core/upsale/hooks';
import { usePayment } from 'core/payment/payment-processor-provider/paymentProcessorContext';
import { ProductDetails } from 'core/store/billing/selectors';
import { PaymentSystem } from '@web-solutions/react-billing';
import getLocale from 'src/localization/get-locale';
import SuccessPayment from '../webUpsale/components/successPaymentPopup';
import Button from 'src/onboarding/components/ui/button/Button';
import { CheckIcon } from 'src/onboarding/assets/icons/CheckIcon';
import FailedPayment from '../webUpsale/components/failedPaymentPopup';

const TIMER_SECONDS = 5 * 60;

const UpsaleDynamic = () => {
  const { onNext } = useNav();
  const { upsaleDynamic } = useRemoteConfig();
  const { callbacksRef } = usePayment();
  const locale = getLocale();

  const product = upsaleDynamic.products.product[0];

  const [showFailedPopup, setShowFailedPopup] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [loading, setLoading] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(TIMER_SECONDS);

  // ── Countdown timer ──────────────────────────────────────────────────────
  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [secondsLeft]);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60)
      .toString()
      .padStart(2, '0');
    const sec = (s % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
  };

  // ── Payment hooks ─────────────────────────────────────────────────────────
  const { handleClick, handleActiveProduct, handleOpen } = useUpsaleMethods({ product });

  useEffect(() => {
    handleActiveProduct();
    handleOpen();
  }, []);

  useEffect(() => {
    callbacksRef.current = {
      onSuccess: (data) => {
        console.log('upsale success', data);
        setShowSuccessPopup(true);
        setLoading(false);

        setTimeout(() => setShowCheck(true), 400);
        setTimeout(() => onNext(), 1800);
      },
      onError: (data) => {
        console.log('upsale error', data);
        setLoading(false);

        setShowFailedPopup(true);
      },
    };
  }, []);

  const handleRetry = () => {
    setShowFailedPopup(false);
    handleClick();
  };

  const handleSuccessClose = () => {
    setShowSuccessPopup(false);
    setShowCheck(false);
    onNext();
  };

  // ── Localised strings ─────────────────────────────────────────────────────
  const t = (field: Record<string, string>) => field?.[locale] || field?.en || '';

  const title = t(upsaleDynamic.title);
  const subTitle = t(upsaleDynamic.subTitle);
  const descText = t(upsaleDynamic.descriptionText);
  const timerLabel = t(upsaleDynamic.timerLabel);
  const priceNote = t(upsaleDynamic.priceNote);
  const skipText = t(upsaleDynamic.skipText);
  const buttonTitle = t(product.buttonTitle);
  const features: string[] =
    upsaleDynamic.whatYouGetText?.[locale] || upsaleDynamic.whatYouGetText?.en || [];

  const discountPct = product.oldAmount
    ? Math.round((1 - Number(product.amount) / Number(product.oldAmount)) * 100)
    : null;

  return (
    <div className={styles.container}>
      {/* ── Timer Banner ────────────────────────────────────────────────── */}
      <div className={styles.timerBanner}>
        <span className={styles.timerText}>{timerLabel}</span>
        <span className={`${styles.timerBadge} ${secondsLeft === 0 ? styles.timerExpired : ''}`}>
          {formatTime(secondsLeft)}
        </span>
      </div>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <div className={styles.hero}>
        <h1 className={styles.heroTitle}>{title}</h1>
        <p className={styles.heroSubtitle}>{subTitle}</p>
        <p className={styles.descText}>{descText}</p>
      </div>

      {/* ── Feature card ────────────────────────────────────────────────── */}
      <div className={styles.content}>
        <div className={styles.featureCard}>
          {features.map((item: string, idx: number) => (
            <div key={idx} className={styles.featureRow}>
              <CheckIcon />
              {/* <span className={styles.checkIcon}>✓</span> */}
              <span className={styles.featureText}>{item}</span>
            </div>
          ))}

          <div className={styles.priceBlock}>
            {discountPct && <span className={styles.discountBadge}>🔥 {discountPct}% OFF</span>}
            {product.oldAmount && <span className={styles.oldPrice}>${product.oldAmount}</span>}
            <span className={styles.price}>${product.amount}</span>
            <span className={styles.priceNote}>{priceNote}</span>
          </div>
        </div>
      </div>

      {/* ── Footer CTA ──────────────────────────────────────────────────── */}
      <div className={styles.footer}>
        <Button
          title={buttonTitle}
          isLoading={loading}
          onClick={() => {
            setLoading(true);
            handleClick();
          }}
        />
        <Button type='secondary' title={skipText} onClick={onNext} />
      </div>

      {showSuccessPopup && <SuccessPayment onClose={handleSuccessClose} showCheck={showCheck} />}
      {showFailedPopup && (
        <FailedPayment
          onClose={() => setShowFailedPopup(false)}
          onRetry={handleRetry}
          loading={loading}
        />
      )}
    </div>
  );
};

export default UpsaleDynamic;

const mappingRemoteConfigToBillingFields = (
  product: any,
  paymentSystemType: PaymentSystem
): ProductDetails => {
  return {
    id: paymentSystemType === PaymentSystem.TRUEGATE ? product?.productCode : product?.solidgateId,
    priceText: product?.amount,
    amount: product?.amount,
    currency: product?.currency,
    product:
      paymentSystemType === PaymentSystem.TRUEGATE ? product?.productCode : product?.solidgateId,
    isOneTimePurchase: true,
    ...(product?.trigger && { trigger: product.trigger }),
  } as unknown as ProductDetails;
};
